/* import __COLOCATED_TEMPLATE__ from './export-schedule-frequency-item.hbs'; */
/* RESPONSIBLE TEAM: team-reporting */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import formatTimezoneToUTC from 'embercom/lib/format-timezone-to-utc';
import moment from 'moment-timezone';
import { cached } from 'tracked-toolbox';
import { action } from '@ember/object';
import type DatasetExportSchedule from 'embercom/models/reporting/dataset-export-schedule';
import { indexBy } from 'underscore';
import { DayPosition, SchedulePreset } from 'embercom/models/reporting/dataset-export-schedule';
import { isEmpty } from '@ember/utils';
interface Args {
  text: string;
  schedule: DatasetExportSchedule;
  onSelect: () => void;
  isSelected: boolean;
}

export default class ExportScheduleFrequencyItem extends Component<Args> {
  @service declare intl: any;
  @service declare appService: $TSFixMe;

  declare exportRuleSnapshot: DatasetExportSchedule['exportRule'];

  get selectedCadence() {
    return this.args.schedule.exportRule.interval.step;
  }

  get timezone() {
    return this.args.schedule.timezone;
  }

  get frequencies() {
    let basePath = 'reporting.data-export.schedule-export.new-schedule.custom-frequency';
    return [
      {
        text: this.intl.t(`${basePath}.weeks`),
        value: SchedulePreset.WEEKLY,
      },
      {
        text: this.intl.t(`${basePath}.months`),
        value: SchedulePreset.MONTHLY,
      },
    ];
  }

  get daysOfWeek() {
    let days = [
      { text: this.intl.t('app.lib.reporting.week-days.sunday'), value: 0 },
      { text: this.intl.t('app.lib.reporting.week-days.monday'), value: 1 },
      { text: this.intl.t('app.lib.reporting.week-days.tuesday'), value: 2 },
      { text: this.intl.t('app.lib.reporting.week-days.wednesday'), value: 3 },
      { text: this.intl.t('app.lib.reporting.week-days.thursday'), value: 4 },
      { text: this.intl.t('app.lib.reporting.week-days.friday'), value: 5 },
      { text: this.intl.t('app.lib.reporting.week-days.saturday'), value: 6 },
    ];
    return days.map((day) => ({
      ...day,
      isSelected: this.args.schedule.exportRule.daysOfWeek.includes(day.value),
    }));
  }

  @cached
  get hours() {
    let timezoneString = formatTimezoneToUTC(this.timezone);
    return Array.from(Array(24), (_, i) => ({
      text: `${moment({ hour: i }).format('h:mmA')} ${timezoneString}`,
      value: i,
    }));
  }

  get selectedTime() {
    return this.args.schedule.exportRule.deliveryTime.hour;
  }

  @action
  onDaysSelectionChange(value: number[]) {
    if (value.length > 0) {
      this.args.schedule.exportRule = {
        ...this.args.schedule.exportRule,
        daysOfWeek: value,
      };
    }
  }

  @action
  onStepSelectionChange(value: string) {
    let oldExportRule = this.args.schedule.exportRule;
    let dayPosition = oldExportRule.interval.dayPosition;
    if (value === SchedulePreset.MONTHLY) {
      // when user selects monthly, we can only select one day of the week.
      // day postion is defaulted to last if none is selected
      this.args.schedule.exportRule = {
        ...oldExportRule,
        daysOfWeek: [this.firstWeekDayOrWeekend],
        interval: {
          ...oldExportRule.interval,
          step: value,
          dayPosition: dayPosition ?? DayPosition.LAST,
        },
      };
    } else {
      this.args.schedule.exportRule = {
        ...oldExportRule,
        interval: {
          ...oldExportRule.interval,
          step: value,
          dayPosition: undefined,
        },
      };
    }
  }

  get firstWeekDay() {
    return this.args.schedule.exportRule.daysOfWeek.find((day) => day > 0 && day < 6);
  }

  get firstWeekDayOrWeekend() {
    return this.firstWeekDay ?? this.args.schedule.exportRule.daysOfWeek[0];
  }

  get isMonthlyStep() {
    return this.args.schedule.exportRule.interval.step === SchedulePreset.MONTHLY;
  }

  @action
  onTimeSelectionChange(timeIndex: number) {
    this.args.schedule.exportRule = {
      ...this.args.schedule.exportRule,
      deliveryTime: {
        hour: timeIndex,
        minute: 0,
        timezone: this.timezone,
      },
    };
  }

  get selectedDaysLabel() {
    let indexedDays = indexBy(this.daysOfWeek, 'value');
    let label = this.args.schedule.exportRule.daysOfWeek.map((day) => indexedDays[day].text);
    if (label.length > 2) {
      return `${label.slice(0, 4).join(', ')}...`;
    }
    return label.join(', ');
  }

  @action
  updateIntervalValue(e: Event) {
    let value = (e.target as HTMLInputElement).value;
    if (!isNaN(Number(value))) {
      this.args.schedule.exportRule = {
        ...this.args.schedule.exportRule,
        interval: {
          ...this.args.schedule.exportRule.interval,
          value: Number(value),
        },
      };
    }
  }

  get applyButtonDisabled() {
    return isEmpty(this.args.schedule.exportRule.interval.value);
  }

  @action
  snapshotExportRule() {
    this.exportRuleSnapshot = this.args.schedule.exportRule;
  }

  @action
  onCancel() {
    this.args.schedule.exportRule = this.exportRuleSnapshot;
  }

  @action
  onDayPositionChange(value: DayPosition[]) {
    this.args.schedule.exportRule = {
      ...this.args.schedule.exportRule,
      interval: {
        ...this.args.schedule.exportRule.interval,
        dayPosition: value[0],
      },
    };
  }

  get exportRule() {
    return this.args.schedule.exportRule;
  }

  get selectedDayPositionLabel() {
    let indexedDayPositionItems = indexBy(this.dayPositionItems, 'value');
    if (this.exportRule.interval.dayPosition) {
      return indexedDayPositionItems[this.exportRule.interval.dayPosition].text;
    }
    return indexedDayPositionItems[DayPosition.FIRST].text;
  }

  get dayPositionItems() {
    let items = [
      {
        text: this.intl.t('reporting.data-export.schedule-export.new-schedule.day-position.last'),
        value: 'last',
      },
      {
        text: this.intl.t('reporting.data-export.schedule-export.new-schedule.day-position.first'),
        value: 'first',
      },
      {
        text: this.intl.t('reporting.data-export.schedule-export.new-schedule.day-position.second'),
        value: 'second',
      },
      {
        text: this.intl.t('reporting.data-export.schedule-export.new-schedule.day-position.third'),
        value: 'third',
      },
    ];
    return items.map((item) => ({
      ...item,
      isSelected: this.args.schedule.exportRule.interval.dayPosition === item.value,
    }));
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Custom::ExportScheduleFrequencyItem': typeof ExportScheduleFrequencyItem;
    'reporting/custom/export-schedule-frequency-item': typeof ExportScheduleFrequencyItem;
  }
}
